import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  Button,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  useMediaQuery,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import { io } from "socket.io-client";

import EmailPanelIzquierdo from "./EmailPanelIzquierdo";
import EmailPanelDerecho from "./EmailPanelDerecho";
import AttachmentsLibrary from "./AttachmentsLibrary";
import { emailMessagePath, bulkEmailDeletePath } from "../emailApiPaths";

import EmailEnviarPro from "../eNuevo/EmailEnviarPro";
import EmailContestar from "../eContestar/EmailContestar";
import EmailReenviar from "../eReenviar/EmailReenviar";

import AccountManager from "../../cuentas/AccountManager";

import DeleteIcon from "@mui/icons-material/Delete";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import { useUniboxColors } from "../../useUniboxColors";
import {
  buildGroupedThreads,
  collectDeleteTargets,
  LIST_GROUPING_STORAGE_KEY,
  readListGrouping,
  getSortedConversationThreadKeys,
} from "./threadGrouping";
import ToolbarIconButton from "./ToolbarIconButton";

const API = process.env.REACT_APP_API_URL;

function SelectionLayoutToggle({ mode, onChange, vertical = false }) {
  const c = useUniboxColors();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        gap: 0.4,
        ...(vertical
          ? { pt: 0.75, mt: 0.25, borderTop: `1px solid ${c.border}` }
          : { pl: 1, ml: 0.5, borderLeft: `1px solid ${c.border}` }),
      }}
    >
      {[
        { id: "top", label: "A" },
        { id: "side", label: "D" },
      ].map(({ id, label }) => (
        <IconButton
          key={id}
          size="small"
          onClick={() => onChange(id)}
          sx={{
            width: 28,
            height: 28,
            fontSize: 11,
            fontWeight: 800,
            borderRadius: c.radius,
            bgcolor: mode === id ? c.accent : "transparent",
            color: mode === id ? "#fff" : c.textMuted,
            border: `1px solid ${mode === id ? c.accent : c.border}`,
            "&:hover": {
              bgcolor: mode === id ? c.selectedBorder : c.cardHover,
            },
          }}
        >
          {label}
        </IconButton>
      ))}
    </Box>
  );
}

function SelectionActions({
  onDelete,
  onRead,
  onUnread,
  onClear,
  onSelectAll,
  showSelectAll,
  selectAllLabel,
  vertical = false,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: vertical ? "column" : "row",
        alignItems: "center",
        gap: 0.65,
      }}
    >
      {showSelectAll && (
        <ToolbarIconButton
          title={selectAllLabel}
          onClick={onSelectAll}
          icon={<SelectAllIcon sx={{ fontSize: "1.05rem" }} />}
        />
      )}
      <ToolbarIconButton
        title="Eliminar"
        onClick={onDelete}
        variant="danger"
        icon={<DeleteIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Marcar como leído"
        onClick={onRead}
        variant="primary"
        icon={<DoneIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Marcar como no leído"
        onClick={onUnread}
        icon={<MailOutlineIcon sx={{ fontSize: "1.05rem" }} />}
      />
      <ToolbarIconButton
        title="Cancelar selección"
        onClick={onClear}
        icon={<CloseIcon sx={{ fontSize: "1.05rem" }} />}
      />
    </Box>
  );
}

export default function EmailGeneral() {
  const [threads, setThreads] = useState({});
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [openAccounts, setOpenAccounts] = useState(false);

  const [showSendPanel, setShowSendPanel] = useState(false);

  const [showReplyPanel, setShowReplyPanel] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  const [showForwardPanel, setShowForwardPanel] = useState(false);
  const [forwardEmail, setForwardEmail] = useState(null);
  const [forwardHtml, setForwardHtml] = useState("");
  const [forwardOriginalAttachments, setForwardOriginalAttachments] = useState(
    []
  );

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteSingleBusy, setDeleteSingleBusy] = useState(false);
  const [sendNotice, setSendNotice] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [accounts, setAccounts] = useState([]);
  const [authError, setAuthError] = useState(false);
  const [isLoadingEmails, setIsLoadingEmails] = useState(true);

  const modoVista = "email";

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("unread");
  const [listGrouping, setListGrouping] = useState(readListGrouping);
  const [folder, setFolder] = useState("inbox");

  const [attachmentsByEmail, setAttachmentsByEmail] = useState({});
  const [bodyCache, setBodyCache] = useState({});
  const [headerCache, setHeaderCache] = useState({});

  const [selectedEmails, setSelectedEmails] = useState([]); // selección múltiple
  const [selectionMode, setSelectionMode] = useState(false); // si hay o no selección

  const [selectionBarMode, setSelectionBarMode] = useState("top");

  const [showDeleteSelectedDialog, setShowDeleteSelectedDialog] =
    useState(false);
  const [deleteSelectedBusy, setDeleteSelectedBusy] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const c = useUniboxColors();
  const [mobileView, setMobileView] = useState("list");
  const [attachmentsLibraryOpen, setAttachmentsLibraryOpen] = useState(false);
  const [focusGmailId, setFocusGmailId] = useState(null);

  const selectConversation = (key) => {
    if (key !== selectedPerson) {
      setSelectedEmails([]);
      setSelectionMode(false);
      setFocusGmailId(null);
    }
    setSelectedPerson(key);
    if (isMobile) setMobileView("thread");
  };

  const handleMobileBack = () => {
    setMobileView("list");
  };

  useEffect(() => {
    if (!isMobile) setMobileView("list");
  }, [isMobile]);

  /* Al cambiar de conversación o filtro: vaciar selección (evita borrar emails de otro hilo) */
  useEffect(() => {
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [selectedPerson, filter]);

  const threadsRef = useRef({});
  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  const folderRef = useRef(folder);
  useEffect(() => {
    folderRef.current = folder;
  }, [folder]);

  const bodyCacheRef = useRef({});
  const bodyInflightRef = useRef(new Map());
  const headerCacheRef = useRef({});
  useEffect(() => {
    bodyCacheRef.current = bodyCache;
  }, [bodyCache]);
  useEffect(() => {
    headerCacheRef.current = headerCache;
  }, [headerCache]);

  useEffect(() => {
    setBodyCache((prev) => {
      const keys = Object.keys(prev || {});
      if (!keys.length) return prev;
      const next = { ...prev };
      let changed = false;
      for (const key of keys) {
        if (String(next[key] || "").includes("Vista previa del índice")) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const attachmentsByEmailRef = useRef({});
  useEffect(() => {
    attachmentsByEmailRef.current = attachmentsByEmail;
  }, [attachmentsByEmail]);

  const bulkDeleteInflightRef = useRef(0);
  const socketReloadTimerRef = useRef(null);

  /* ---------------- LOAD ACCOUNTS (Sync del menú usa este estado) ---------------- */
  const reloadAccounts = useCallback(() => {
    axios
      .get(`${API}/auth/accounts`)
      .then((res) => {
        setAccounts(res.data);
        setAuthError(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setAuthError(true);
          return;
        }
        console.error("Error cargando cuentas:", err);
      });
  }, []);

  useEffect(() => {
    reloadAccounts();
  }, [reloadAccounts]);

  /* Auto-sync IMAP — retrasado para no bloquear la lectura (mail.dlx.dk = 1 conexión) */
  const imapSyncInflightRef = useRef(false);
  useEffect(() => {
    const imapAccounts = (accounts || []).filter(
      (a) => String(a.account_type || "").toLowerCase() === "imap"
    );
    if (!imapAccounts.length || imapSyncInflightRef.current) return;

    const storageKey = "unibox_imap_autosync_ts";
    const last = Number(localStorage.getItem(storageKey) || 0);
    const minGapMs = 5 * 60 * 1000;
    if (Date.now() - last < minGapMs) return;

    const delayMs = 5 * 60 * 1000;
    const timer = setTimeout(() => {
      if (imapSyncInflightRef.current) return;
      imapSyncInflightRef.current = true;
      localStorage.setItem(storageKey, String(Date.now()));

      (async () => {
        try {
          for (const acc of imapAccounts) {
            try {
              await axios.get(`${API}/gmail/sync/${acc.email}`);
            } catch (err) {
              console.warn("IMAP auto-sync falló:", acc.email, err);
            }
          }
        } finally {
          imapSyncInflightRef.current = false;
        }
      })();
    }, delayMs);

    return () => clearTimeout(timer);
  }, [accounts, API]);

  /* ---------------- LOAD EMAILS ---------------- */
  const loadEmails = useCallback(async (folderOverride) => {
    const activeFolder = folderOverride || folderRef.current;
    const alreadyHaveThreads =
      Object.keys(threadsRef.current || {}).length > 0;
    if (!alreadyHaveThreads) {
      setIsLoadingEmails(true);
    }
    try {
      const res = await axios.get(`${API}/emails-from-db`, {
        params: { folder: activeFolder, _ts: Date.now() },
      });
      const data = res.data;

      const summary = await axios
        .get(`${API}/emails/attachments-summary`)
        .catch(() => ({ data: {} }));
      const hasAttachmentsMap = summary.data || {};

      // Marca emails con adjuntos
      for (const person in data) {
        data[person] = data[person].map((email) => ({
          ...email,
          hasAttachments: (hasAttachmentsMap[email.gmailId] || 0) > 0,
        }));
      }

      setThreads(data);
      setAuthError(false);
    } catch (err) {
      if (err?.response?.status === 401) {
        setAuthError(true);
        return;
      }
      console.error("Error cargando emails:", err);
    } finally {
      setIsLoadingEmails(false);
    }
  }, [API]);

  const applyLocalDelete = useCallback((ids) => {
    const remove = new Set((ids || []).filter(Boolean));
    if (!remove.size) return;

    setThreads((prev) => {
      const updated = { ...prev };
      for (const person of Object.keys(updated)) {
        updated[person] = (updated[person] || []).filter(
          (e) => !remove.has(e.gmailId)
        );
        if (!updated[person].length) delete updated[person];
      }
      return updated;
    });

    setAttachmentsByEmail((prev) => {
      const next = { ...prev };
      for (const id of remove) delete next[id];
      return next;
    });
  }, []);

  const deleteEmailsBulk = useCallback(
    async (ids) => {
      const unique = [...new Set((ids || []).filter(Boolean))];
      if (!unique.length) return { ok: [], failed: [] };

      bulkDeleteInflightRef.current += 1;

      try {
        const mode = folderRef.current === "trash" ? "permanent" : "trash";
        let data;

        try {
          const res = await axios.post(bulkEmailDeletePath(API, mode), {
            gmailIds: unique,
          });
          data = res.data;
        } catch (err) {
          const status = err?.response?.status;
          const useIndividual =
            status === 404 || status === 500 || status === 502;
          if (!useIndividual) throw err;
          const deleteOne =
            mode === "permanent"
              ? (id) => axios.delete(emailMessagePath(API, id, "permanent"))
              : (id) => axios.post(emailMessagePath(API, id, "trash"));
          const results = await Promise.all(
            unique.map((id) =>
              deleteOne(id)
                .then(() => id)
                .catch(() => null)
            )
          );
          const ok = results.filter(Boolean);
          data = { ok, failed: unique.filter((id) => !ok.includes(id)) };
        }

        const succeeded =
          data?.ok?.length > 0
            ? data.ok
            : unique.filter((id) => !(data?.failed || []).includes(id));

        applyLocalDelete(succeeded);

        if ((data?.failed || []).length) {
          loadEmails().catch((err) =>
            console.error("Error reconciliando borrado parcial:", err)
          );
        }

        return data;
      } finally {
        bulkDeleteInflightRef.current = Math.max(
          0,
          bulkDeleteInflightRef.current - 1
        );
      }
    },
    [API, applyLocalDelete, loadEmails]
  );

  const scheduleSocketReload = useCallback(() => {
    if (bulkDeleteInflightRef.current > 0) return;
    if (socketReloadTimerRef.current) {
      clearTimeout(socketReloadTimerRef.current);
    }
    socketReloadTimerRef.current = setTimeout(() => {
      setAttachmentsByEmail({});
      setHeaderCache({});
      loadEmails().catch((err) =>
        console.error("Error recargando tras socket:", err)
      );
    }, 700);
  }, [loadEmails]);

  const restoreEmail = useCallback(
    async (id) => {
      if (!id) return;
      try {
        await axios.post(emailMessagePath(API, id, "restore"));
        folderRef.current = "inbox";
        setFolder("inbox");
        setFilter("all");
        await loadEmails("inbox");
        setSendNotice({
          open: true,
          message: "Correo restaurado a la bandeja.",
          severity: "success",
        });
      } catch (err) {
        console.error("Error restaurando correo:", err);
        setSendNotice({
          open: true,
          message:
            err?.response?.data?.error ||
            "No se pudo restaurar el correo desde la papelera.",
          severity: "error",
        });
        await loadEmails();
      }
    },
    [API, loadEmails]
  );

  const notifyEmailSent = useCallback(
    (message = "Correo enviado correctamente", sentMeta = null) => {
      setSendNotice({ open: true, message, severity: "success" });
      setBodyCache({});
      setHeaderCache({});
      const gmailId = sentMeta?.gmailId;
      if (gmailId) {
        setAttachmentsByEmail((prev) => {
          const next = { ...prev };
          delete next[gmailId];
          return next;
        });
      }
      loadEmails().catch((err) =>
        console.error("Error recargando tras envío:", err)
      );
    },
    [loadEmails]
  );

  const conversationThreads = React.useMemo(
    () => buildGroupedThreads(threads, listGrouping),
    [threads, listGrouping]
  );

  const findThreadKeyForGmailId = useCallback(
    (gmailId) => {
      if (!gmailId) return null;
      for (const [key, emails] of Object.entries(conversationThreads || {})) {
        if ((emails || []).some((e) => e.gmailId === gmailId)) return key;
      }
      return null;
    },
    [conversationThreads]
  );

  const openAttachmentInConversation = useCallback(
    (item) => {
      const gmailId = item?.gmailId;
      if (!gmailId) return;

      const threadKey = findThreadKeyForGmailId(gmailId);
      if (!threadKey) {
        window.alert(
          "No se encontró la conversación de este archivo. Prueba a sincronizar la cuenta."
        );
        return;
      }

      setAttachmentsLibraryOpen(false);
      if (folder === "trash") setFolder("inbox");
      selectConversation(threadKey);
      setFocusGmailId(gmailId);
    },
    [findThreadKeyForGmailId, folder]
  );

  const visibleThreadEmailIds = useMemo(() => {
    if (!selectedPerson) return [];
    const raw = conversationThreads[selectedPerson];
    let list = Array.isArray(raw) ? [...raw] : [];
    if (filter === "read") list = list.filter((e) => e.isRead);
    if (filter === "unread") list = list.filter((e) => !e.isRead);
    return list.map((e) => e.gmailId).filter(Boolean);
  }, [conversationThreads, selectedPerson, filter]);

  const selectedInVisibleThread = useMemo(() => {
    const allowed = new Set(visibleThreadEmailIds);
    return selectedEmails.filter((id) => allowed.has(id));
  }, [selectedEmails, visibleThreadEmailIds]);

  const allInThreadSelected =
    visibleThreadEmailIds.length > 0 &&
    visibleThreadEmailIds.every((id) => selectedInVisibleThread.includes(id));

  useEffect(() => {
    const ordered = getSortedConversationThreadKeys(conversationThreads);
    if (!ordered.length) {
      if (selectedPerson) setSelectedPerson(null);
      return;
    }

    if (selectedPerson && conversationThreads[selectedPerson]?.length) return;

    setSelectedPerson(ordered[0]);
  }, [conversationThreads, selectedPerson]);

  const listGroupingInitialized = useRef(false);

  useEffect(() => {
    try {
      localStorage.setItem(LIST_GROUPING_STORAGE_KEY, listGrouping);
    } catch {
      /* ignore */
    }

    if (!listGroupingInitialized.current) {
      listGroupingInitialized.current = true;
      return;
    }

    setSelectedPerson(null);
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [listGrouping]);

  useEffect(() => {
    loadEmails();
  }, [folder, loadEmails]);

  useEffect(() => {
    if (folder === "trash" && filter !== "all") {
      setFilter("all");
    }
    setSelectedPerson(null);
    setSelectedEmails([]);
    setSelectionMode(false);
  }, [folder]);

  /* ---------------- SOCKET SYNC ---------------- */
  useEffect(() => {
    const socket = io(API);
    socket.on("emails-updated", () => {
      scheduleSocketReload();
    });
    socket.on("imap-sync-finished", (payload) => {
      if (!payload) return;
      const errEmail = String(payload.email || "").toLowerCase();
      const isMine = (accounts || []).some(
        (a) => String(a.email || "").toLowerCase() === errEmail
      );
      if (payload.ok === false && payload.error) {
        if (!payload.userTriggered || !isMine) return;
        setSendNotice({
          open: true,
          severity: "warning",
          message: `IMAP ${payload.email || ""}: ${payload.error}`,
        });
        return;
      }
      if (payload.ok) {
        loadEmails();
      }
    });
    return () => {
      if (socketReloadTimerRef.current) {
        clearTimeout(socketReloadTimerRef.current);
      }
      socket.disconnect();
    };
  }, [API, loadEmails, accounts, scheduleSocketReload]);

  /* ---------------- LOAD BODY ---------------- */
  const loadBodyCached = useCallback(async (gmailId, { retry = true } = {}) => {
    if (!gmailId) return "";
    const cachedBody = bodyCacheRef.current[gmailId];
    const cachedLooksPartial =
      typeof cachedBody === "string" &&
      cachedBody.includes("Vista previa del índice");
    if (
      cachedBody !== undefined &&
      cachedBody !== null &&
      !cachedLooksPartial &&
      Object.prototype.hasOwnProperty.call(bodyCacheRef.current, gmailId)
    ) {
      return cachedBody;
    }

    const inflight = bodyInflightRef.current.get(gmailId);
    if (inflight) return inflight;

    const isImap = String(gmailId).startsWith("imap:");
    const requestTimeout = isImap ? 45_000 : 60_000;
    const maxAttempts = retry && isImap ? 2 : 1;

    const run = (async () => {
      let lastErr;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const res = await axios.get(emailMessagePath(API, gmailId, "body"), {
            timeout: requestTimeout,
          });
          const html = res.data.html || res.data.text || "";
          const headers = res.data.headers || {};

          setBodyCache((prev) => {
            const next = { ...prev, [gmailId]: html };
            for (const key of Object.keys(next)) {
              if (
                key !== gmailId &&
                String(next[key] || "").includes("Vista previa del índice")
              ) {
                delete next[key];
              }
            }
            return next;
          });
          setHeaderCache((prev) => ({
            ...prev,
            [gmailId]: {
              to: headers.to || prev[gmailId]?.to || "",
              cc: headers.cc || prev[gmailId]?.cc || "",
              bcc: headers.bcc || prev[gmailId]?.bcc || "",
            },
          }));
          return html;
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const retryable = status === 503;
          if (retryable && attempt + 1 < maxAttempts) {
            const waitMs = 3000;
            console.warn(
              `body ${gmailId} intento ${attempt + 1}/${maxAttempts}, reintento en ${waitMs}ms`,
              status || err?.code
            );
            await new Promise((r) => setTimeout(r, waitMs));
            continue;
          }
          break;
        }
      }
      console.error(lastErr);
      setBodyCache((prev) => ({ ...prev, [gmailId]: "" }));
      return "";
    })();

    bodyInflightRef.current.set(gmailId, run);
    try {
      return await run;
    } finally {
      if (bodyInflightRef.current.get(gmailId) === run) {
        bodyInflightRef.current.delete(gmailId);
      }
    }
  }, [API]);

  /* ---------------- LOAD ATTACHMENTS ---------------- */
  const attachmentsInflightRef = useRef(new Set());
  const loadAttachments = useCallback(async (gmailId, { force = false } = {}) => {
    if (!gmailId) return;
    if (!force && attachmentsByEmailRef.current[gmailId] !== undefined) return;
    if (attachmentsInflightRef.current.has(gmailId)) return;
    attachmentsInflightRef.current.add(gmailId);
    try {
      const res = await axios.get(emailMessagePath(API, gmailId, "attachments"), {
        timeout: 20_000,
      });
      const files = Array.isArray(res.data) ? res.data : [];
      setAttachmentsByEmail((prev) => ({ ...prev, [gmailId]: files }));
    } catch (err) {
      console.error(err);
      if (force) {
        setAttachmentsByEmail((prev) => {
          const next = { ...prev };
          delete next[gmailId];
          return next;
        });
      }
    } finally {
      attachmentsInflightRef.current.delete(gmailId);
    }
  }, [API]);

  /* ---------------- TOGGLE READ ---------------- */
  const toggleRead = async (gmailId) => {
    const res = await axios.post(emailMessagePath(API, gmailId, "toggle-read"));
    const newState = res.data.isRead;

    setThreads((prev) => {
      const updated = { ...prev };
      for (const p in updated) {
        updated[p] = updated[p].map((e) =>
          e.gmailId === gmailId ? { ...e, isRead: newState } : e
        );
      }
      return updated;
    });
  };

  /* ---------------- DELETE ---------------- */
  const borrar = async () => {
    if (!deleteId || deleteSingleBusy) return;
    const targets = collectDeleteTargets(
      conversationThreads[selectedPerson],
      deleteId
    );
    try {
      setDeleteSingleBusy(true);
      await deleteEmailsBulk(targets);
      setShowDeleteDialog(false);
      setDeleteId(null);
      const threadEmails = conversationThreads[selectedPerson] || [];
      if (threadEmails.every((e) => targets.includes(e.gmailId))) {
        setSelectedPerson(null);
      }
    } catch (err) {
      console.error("Error al eliminar email:", err);
      setSendNotice({
        open: true,
        message:
          err?.response?.data?.error ||
          "No se pudo eliminar el correo. Prueba de nuevo.",
        severity: "error",
      });
    } finally {
      setDeleteSingleBusy(false);
    }
  };

  /* ---------------- OPEN FORWARD ---------------- */
  const openForward = async (email) => {
    setForwardEmail(email);

    const res = await axios.get(
      emailMessagePath(API, email.gmailId, "attachments/full")
    );
    const originalFiles = res.data;

    const body = await loadBodyCached(email.gmailId);

    const forwardedBlock = `
      <br/>
      ---------- Mensaje reenviado ----------<br/>
      <b>De:</b> ${email.from}<br/>
      <b>Fecha:</b> ${new Date(email.date).toLocaleString("es-ES")}<br/>
      <b>Asunto:</b> ${email.subject}<br/>
      <b>Para:</b> ${email.to_email || email.to || ""}<br/>
      ${email.cc_email ? `<b>CC:</b> ${email.cc_email}<br/>` : ""}
      <br/>
      ${body}
    `;

    setForwardHtml(forwardedBlock);
    setForwardOriginalAttachments(originalFiles);
    setShowForwardPanel(true);
  };

  function stringToPastelColor(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 70%, 80%)`;
  }

  function getAccountColor(email) {
    const account = accounts.find((a) => a.id === email.account_id);
    if (!account) return "#e2e8f0"; // gris claro

    return stringToPastelColor(account.email);
  }

  const toggleSelectEmail = (gmailId) => {
    setSelectedEmails((prev) => {
      if (prev.includes(gmailId)) {
        const updated = prev.filter((id) => id !== gmailId);
        if (updated.length === 0) setSelectionMode(false);
        return updated;
      } else {
        setSelectionMode(true);
        return [...prev, gmailId];
      }
    });
  };

  const clearSelection = () => {
    setSelectedEmails([]);
    setSelectionMode(false);
  };

  const toggleSelectAllInThread = useCallback(() => {
    if (allInThreadSelected) {
      clearSelection();
      return;
    }
    if (visibleThreadEmailIds.length === 0) return;
    setSelectedEmails(visibleThreadEmailIds);
    setSelectionMode(true);
  }, [allInThreadSelected, visibleThreadEmailIds]);

  const confirmDeleteSelected = async () => {
    const ids = selectedInVisibleThread;
    if (!ids.length) {
      clearSelection();
      setShowDeleteSelectedDialog(false);
      return;
    }
    if (deleteSelectedBusy) return;

    try {
      setDeleteSelectedBusy(true);
      await deleteEmailsBulk(ids);
      clearSelection();
      setShowDeleteSelectedDialog(false);
    } catch (err) {
      console.error("Error al eliminar emails:", err);
      loadEmails();
    } finally {
      setDeleteSelectedBusy(false);
    }
  };

  const deleteConversationThreads = async (threadKeys) => {
    const keys = Array.isArray(threadKeys) ? threadKeys : [];
    if (!keys.length) return;

    const ids = keys.flatMap((key) =>
      (conversationThreads[key] || []).map((e) => e.gmailId).filter(Boolean)
    );
    if (!ids.length) return;

    await deleteEmailsBulk(ids);
    clearSelection();

    if (keys.includes(selectedPerson)) {
      setSelectedPerson(null);
    }
  };

  const markSelectedAsRead = async () => {
    for (const id of selectedInVisibleThread) {
      // 1) Pido el estado actual
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      // 2) Si YA está leído → no hago nada
      if (email?.isRead) continue;

      // 3) Sino → toggle
      await axios.post(emailMessagePath(API, id, "toggle-read"));
    }

    clearSelection();
    loadEmails();
  };

  const markSelectedAsUnread = async () => {
    for (const id of selectedInVisibleThread) {
      const email = Object.values(threads)
        .flat()
        .find((e) => e.gmailId === id);

      if (!email || !email.isRead) continue;

      await axios.post(emailMessagePath(API, id, "toggle-read"));
    }

    clearSelection();
    loadEmails();
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        minHeight: { xs: "100dvh", md: "100vh" },
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {authError ? (
        <Box
          sx={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#fff4e5",
            color: "#7a2e0b",
            border: "1px solid #ffd8a8",
            borderRadius: "8px",
            px: 2,
            py: 1,
            zIndex: 9999,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          Clave de acceso no valida. Cierra sesion y vuelve a entrar.
        </Box>
      ) : null}

      {/* PANEL IZQUIERDO */}
      <Box
        sx={{
          display: {
            xs: isMobile && mobileView === "thread" ? "none" : "flex",
            md: "flex",
          },
          flexShrink: 0,
          width: { xs: "100%", md: "auto" },
          minWidth: 0,
          minHeight: 0,
        }}
      >
      <EmailPanelIzquierdo
        threads={conversationThreads}
        selectedPerson={selectedPerson}
        setSelectedPerson={selectConversation}
        search={search}
        setSearch={setSearch}
        filter={filter}
        setFilter={setFilter}
        folder={folder}
        setFolder={setFolder}
        setOpenAccounts={setOpenAccounts}
        setShowSendPanel={setShowSendPanel} // 🔥 AÑADIDO
        accounts={accounts} // 🔥 AÑADIDO
        onEmptyTrash={async () => {
          bulkDeleteInflightRef.current += 1;
          try {
            await axios.delete(`${API}/emails/trash/permanent`);
            setThreads({});
            setSelectedPerson(null);
          } finally {
            bulkDeleteInflightRef.current = Math.max(
              0,
              bulkDeleteInflightRef.current - 1
            );
          }
        }}
        onDeleteConversations={deleteConversationThreads}
        isMobile={isMobile}
        listGrouping={listGrouping}
        setListGrouping={setListGrouping}
        onOpenAttachmentsLibrary={() => setAttachmentsLibraryOpen(true)}
        loadEmails={loadEmails}
      />
      </Box>

      {selectionMode && selectedInVisibleThread.length > 0 && (
        <>
          {selectionBarMode === "top" && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: { xs: 0, md: 340 },
                right: 0,
                height: 52,
                bgcolor: c.overlayBg,
                backdropFilter: "blur(10px)",
                borderBottom: `1px solid ${c.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: { xs: 1.5, md: 2 },
                gap: 1.5,
                zIndex: 30,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 13,
                    color: c.textPrimary,
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedInVisibleThread.length} seleccionado
                  {selectedInVisibleThread.length === 1 ? "" : "s"}
                </Typography>
                {visibleThreadEmailIds.length > 1 && !isMobile && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SelectAllIcon sx={{ fontSize: 16 }} />}
                    onClick={toggleSelectAllInThread}
                    sx={{
                      textTransform: "none",
                      fontSize: 11,
                      py: 0.35,
                      px: 1.25,
                      borderRadius: c.radius,
                      borderColor: c.border,
                      color: c.textPrimary,
                      whiteSpace: "nowrap",
                      "&:hover": {
                        borderColor: c.accentBorder,
                        bgcolor: c.accentSoft,
                      },
                    }}
                  >
                    {allInThreadSelected ? "Quitar todos" : "Todos"}
                  </Button>
                )}
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                <SelectionActions
                  onDelete={() => setShowDeleteSelectedDialog(true)}
                  onRead={markSelectedAsRead}
                  onUnread={markSelectedAsUnread}
                  onClear={clearSelection}
                />
                <SelectionLayoutToggle
                  mode={selectionBarMode}
                  onChange={setSelectionBarMode}
                />
              </Box>
            </Box>
          )}

          {selectionBarMode === "side" && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: 16,
                transform: "translateY(-50%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.75,
                bgcolor: c.overlayBg,
                backdropFilter: "blur(10px)",
                p: 0.85,
                borderRadius: c.radiusOuter,
                border: `1px solid ${c.border}`,
                boxShadow: c.shadowMd,
                zIndex: 40,
              }}
            >
              <SelectionActions
                onDelete={() => setShowDeleteSelectedDialog(true)}
                onRead={markSelectedAsRead}
                onUnread={markSelectedAsUnread}
                onClear={clearSelection}
                onSelectAll={toggleSelectAllInThread}
                showSelectAll={visibleThreadEmailIds.length > 1 && !isMobile}
                selectAllLabel={allInThreadSelected ? "Quitar todos" : "Todos"}
                vertical
              />
              <SelectionLayoutToggle
                mode={selectionBarMode}
                onChange={setSelectionBarMode}
                vertical
              />
            </Box>
          )}
        </>
      )}

      {/* PANEL DERECHO */}
      {isLoadingEmails ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
            bgcolor: c.panelAlt,
          }}
        >
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: 13, color: c.textMuted, fontWeight: 600 }}>
            Cargando conversaciones...
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: {
              xs: isMobile && mobileView === "list" ? "none" : "flex",
              md: "flex",
            },
            flex: 1,
            minWidth: 0,
            minHeight: 0,
          }}
        >
        <EmailPanelDerecho
          threads={conversationThreads}
          selectedPerson={selectedPerson}
          filter={filter}
          folder={folder}
          modoVista={modoVista}
          bodyCache={bodyCache}
          headerCache={headerCache}
          attachmentsByEmail={attachmentsByEmail}
          loadAttachments={loadAttachments}
          loadBodyCached={loadBodyCached}
          toggleRead={toggleRead}
          openReply={async (email) => {
            setSelectedEmail(email);
            await loadBodyCached(email.gmailId);
            setShowReplyPanel(true);
          }}
          openForward={openForward}
          askDelete={(id) => {
            setDeleteId(id);
            setShowDeleteDialog(true);
          }}
          restoreEmail={restoreEmail}
          getAccountColor={getAccountColor}
          selectionMode={selectionMode}
          selectedEmails={selectedInVisibleThread}
          toggleSelectEmail={toggleSelectEmail}
          enterSelectionMode={() => setSelectionMode(true)}
          clearSelection={clearSelection}
          selectionBarMode={selectionBarMode}
          isMobile={isMobile}
          onBack={handleMobileBack}
          listGrouping={listGrouping}
          focusGmailId={focusGmailId}
          onFocusGmailIdClear={() => setFocusGmailId(null)}
        />
        </Box>
      )}

      {/* SEND NEW EMAIL */}
      <EmailEnviarPro
        open={showSendPanel}
        onClose={() => setShowSendPanel(false)}
        accounts={accounts}
        onSent={(data) =>
          notifyEmailSent("Correo enviado correctamente", data)
        }
      />

      {/* RESPONDER */}
      <EmailContestar
        open={showReplyPanel}
        onClose={() => {
          setShowReplyPanel(false);
          setSelectedEmail(null);
        }}
        email={selectedEmail}
        accounts={accounts}
        originalHtml={bodyCache[selectedEmail?.gmailId] || ""}
        onSend={async (form) => {
          const res = await axios.post(`${API}/emails/send`, form, {
            timeout: 60000,
          });
          notifyEmailSent("Respuesta enviada correctamente", res.data);
        }}
      />

      {/* REENVIAR */}
      <EmailReenviar
        open={showForwardPanel}
        onClose={() => setShowForwardPanel(false)}
        fromEmail={
          forwardEmail?.accountEmail?.toLowerCase() ||
          accounts[0]?.email?.toLowerCase()
        }
        email={forwardEmail}
        forwardedHtml={forwardHtml}
        forwardOriginalAttachments={forwardOriginalAttachments}
        onSend={async (form) => {
          const res = await axios.post(`${API}/emails/send`, form, {
            timeout: 60000,
          });
          notifyEmailSent("Correo reenviado correctamente", res.data);
        }}
      />

      {/* DELETE DIALOG */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => !deleteSingleBusy && setShowDeleteDialog(false)}
      >
        <Box sx={{ p: 3, minWidth: { xs: 280, sm: 320 } }}>
          <Typography variant="h6">¿Eliminar email?</Typography>
          <Typography sx={{ color: "#777", mt: 1 }}>
            {folder === "trash"
              ? "Esta acción elimina el correo definitivamente."
              : "El correo se moverá a la papelera."}
          </Typography>

          {deleteSingleBusy ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mt: 2,
                py: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2">Eliminando correo...</Typography>
            </Box>
          ) : null}

          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
          >
            <Button
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteSingleBusy}
            >
              Cancelar
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={borrar}
              disabled={deleteSingleBusy}
              startIcon={
                deleteSingleBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {deleteSingleBusy ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ACCOUNTS DRAWER */}
      <Dialog
        open={openAccounts}
        onClose={() => {
          setOpenAccounts(false);
          reloadAccounts();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxWidth: 760,
            maxHeight: "92vh",
            bgcolor: "background.default",
            backgroundImage: "none",
            overflow: "auto",
          },
        }}
      >
        <AccountManager onAccountsChange={reloadAccounts} onImapSynced={loadEmails} />
      </Dialog>

      <AttachmentsLibrary
        open={attachmentsLibraryOpen}
        onClose={() => setAttachmentsLibraryOpen(false)}
        accounts={accounts}
        folder={folder}
        onOpenInConversation={openAttachmentInConversation}
      />

      {/* DELETE SELECTED DIALOG */}
      <Dialog
        open={showDeleteSelectedDialog}
        onClose={() => !deleteSelectedBusy && setShowDeleteSelectedDialog(false)}
      >
        <Box sx={{ p: 3, minWidth: { xs: 280, sm: 360 } }}>
          <Typography variant="h6">¿Eliminar emails seleccionados?</Typography>

          <Typography sx={{ color: "#777", mt: 1 }}>
            {folder === "trash"
              ? `Se eliminarán definitivamente ${selectedInVisibleThread.length} correo(s).`
              : `Se moverán a la papelera ${selectedInVisibleThread.length} correo(s).`}
          </Typography>

          {deleteSelectedBusy ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mt: 2,
                py: 1,
                color: "text.secondary",
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2">Eliminando correos...</Typography>
            </Box>
          ) : null}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              onClick={() => setShowDeleteSelectedDialog(false)}
              disabled={deleteSelectedBusy}
            >
              Cancelar
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={confirmDeleteSelected}
              disabled={deleteSelectedBusy}
              startIcon={
                deleteSelectedBusy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {deleteSelectedBusy ? "Eliminando..." : "Eliminar"}
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Snackbar
        open={sendNotice.open}
        autoHideDuration={5000}
        onClose={() => setSendNotice((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={sendNotice.severity || "success"}
          variant="filled"
          onClose={() => setSendNotice((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {sendNotice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
